/**
 * The three things a surface shows when it is not showing data:
 * Skeleton (waiting), EmptyState (nothing to show), Callout (something to say).
 *
 * 49 files spin their own `animate-pulse` bar and 65 hand-roll a
 * `text-center py-12` placeholder. Both are load-bearing UI — an empty state is
 * often the first thing a new user sees — and both were being improvised.
 *
 * Callout carries the investor deck's signature: a coloured left edge on a
 * tinted ground, drawn with an inset shadow rather than a border so the
 * unlayered `.dark * { border-color }` rule cannot flatten it (see ./foundation).
 */
import React from 'react';
import { accentShadow, type Tone } from './foundation';

/* ── Skeleton ───────────────────────────────────────────────────────────── */

export interface SkeletonProps {
  /** Any CSS width. Vary it across lines so a block does not read as a bar chart. */
  width?: string | number;
  height?: string | number;
  /** Circle instead of a bar — for an avatar or icon placeholder. */
  circle?: boolean;
  className?: string;
}

export function Skeleton({ width = '100%', height = '0.75rem', circle, className = '' }: SkeletonProps) {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse bg-[var(--hover-lift)] ${circle ? 'rounded-full' : ''} ${className}`}
      style={{ width, height, ...(circle ? { aspectRatio: '1' } : {}) }}
    />
  );
}

export interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

/** A paragraph-shaped placeholder. The last line is short, which is what makes
    it read as text rather than as a stack of bars. */
export function SkeletonText({ lines = 3, className = '' }: SkeletonTextProps) {
  return (
    <div className={`flex flex-col gap-2 ${className}`} role="status" aria-label="Loading">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} width={i === lines - 1 ? '60%' : '100%'} />
      ))}
    </div>
  );
}

/* ── EmptyState ─────────────────────────────────────────────────────────── */

export interface EmptyStateProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** A lucide icon component, e.g. `icon={ListChecks}`. */
  icon?: React.ComponentType<{ className?: string }>;
  /** The one thing to do next. An empty state without an action is a dead end. */
  action?: React.ReactNode;
  /** `inline` for inside a panel or table; `page` for a whole empty screen. */
  size?: 'inline' | 'page';
  className?: string;
}

export function EmptyState({
  title,
  description,
  icon: Icon,
  action,
  size = 'inline',
  className = '',
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center text-center ${
        size === 'page' ? 'px-6 py-20' : 'px-4 py-12'
      } ${className}`}
    >
      {Icon && <Icon className="mb-3 h-8 w-8 text-[var(--text-dim)]" />}
      <p
        className={`text-[var(--text-pri)] ${
          size === 'page' ? 'font-serif text-xl' : 'text-sm font-medium'
        }`}
      >
        {title}
      </p>
      {description && (
        <p className="mt-1.5 max-w-[46ch] text-sm leading-relaxed text-[var(--text-ter)]">
          {description}
        </p>
      )}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/* ── Callout ────────────────────────────────────────────────────────────── */

export interface CalloutProps {
  tone?: Tone;
  /** Mono eyebrow above the body — the deck's label column. */
  label?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** Rendered at the right edge, vertically centred. */
  action?: React.ReactNode;
  className?: string;
}

const CALLOUT_FILL: Record<Tone, string> = {
  green: 'bg-[var(--green-dim)]',
  mustard: 'bg-[var(--mustard-dim)]',
  slate: 'bg-[var(--slate-dim)]',
  coral: 'bg-[var(--coral-dim)]',
  neutral: 'bg-[var(--surface)]',
};

const CALLOUT_LABEL: Record<Tone, string> = {
  green: 'text-[var(--green)] dark:text-[var(--text-ter)]',
  mustard: 'text-[var(--mustard)]',
  slate: 'text-[var(--text-ter)]',
  coral: 'text-[var(--coral)]',
  neutral: 'text-[var(--text-ter)]',
};

export function Callout({
  tone = 'neutral',
  label,
  title,
  children,
  action,
  className = '',
}: CalloutProps) {
  return (
    <div
      role={tone === 'coral' ? 'alert' : undefined}
      className={`flex items-start gap-4 px-4 py-3 ${CALLOUT_FILL[tone]} ${className}`}
      style={accentShadow(tone)}
    >
      <div className="min-w-0 flex-1">
        {label && (
          <p className={`mb-1 font-mono text-[0.72rem] uppercase tracking-[0.12em] ${CALLOUT_LABEL[tone]}`}>
            {label}
          </p>
        )}
        {title && <p className="text-sm font-medium text-[var(--text-pri)]">{title}</p>}
        {children && (
          <div className={`text-sm leading-relaxed text-[var(--text-sec)] ${title ? 'mt-1' : ''}`}>
            {children}
          </div>
        )}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
